import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import authService from '../services/authService';
import Navbar from './Navbar';
import Footer from './Footer';

const DiscussionDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [discussion, setDiscussion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [replyContent, setReplyContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [userVote, setUserVote] = useState(null);
  const currentUser = authService.getCurrentUser();

  useEffect(() => {
    fetchDiscussion();
  }, [slug]);

  const fetchDiscussion = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/discussions/${slug}`);
      const data = await response.json();

      if (data.success) {
        setDiscussion(data.data);
        
        // Fetch user vote if authenticated
        if (authService.isAuthenticated()) {
          fetchUserVote();
        }
      } else {
        console.error('Discussion not found');
      }
    } catch (err) {
      console.error('Error fetching discussion:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserVote = async () => {
    try {
      const response = await authService.get(`/api/discussions/${slug}/vote`);
      if (response.success) {
        setUserVote(response.data.userVote);
      }
    } catch (err) {
      console.error('Error fetching user vote:', err);
    }
  };

  const handleVote = async (voteType) => {
    if (!authService.isAuthenticated()) {
      navigate('/login');
      return;
    }

    try {
      const response = await authService.post(`/api/discussions/${slug}/vote`, {
        voteType
      });

      if (response.success) {
        // Update discussion vote counts
        setDiscussion({
          ...discussion,
          upvotes: response.data.upvotes,
          downvotes: response.data.downvotes
        });
        
        // Update user vote
        setUserVote(response.data.userVote);
      }
    } catch (err) {
      console.error('Error voting:', err);
    }
  };

  const handleSubmitReply = async (e) => {
    e.preventDefault();

    if (!authService.isAuthenticated()) {
      navigate('/login');
      return;
    }

    if (!replyContent.trim()) return;

    try {
      setSubmitting(true);
      const response = await authService.post(`/api/discussions/${discussion.id}/replies`, {
        content: replyContent
      });

      if (response.data.success) {
        setReplyContent('');
        fetchDiscussion(); // Refresh to show new reply
      }
    } catch (err) {
      console.error('Error submitting reply:', err);
      alert('Failed to submit reply');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <span className="loading loading-spinner loading-lg"></span>
      </div>
    );
  }

  if (!discussion) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="alert alert-error">
          <span>Discussion not found</span>
        </div>
        <Link to="/discussions" className="btn btn-primary mt-4">
          Back to Discussions
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-base-200">
      <Navbar />
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Breadcrumb */}
        <div className="text-sm breadcrumbs mb-4">
          <ul>
            <li><Link to="/">Home</Link></li>
            <li><Link to="/discussions">Discussions</Link></li>
            <li>{discussion.title}</li>
          </ul>
        </div>

        {/* Main Discussion */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-base-100 rounded-lg shadow-lg overflow-hidden mb-6"
        >
          <div className="flex">
            {/* Voting Section - Left Side */}
            <div className="flex flex-col items-center bg-base-200 px-4 py-6">
              <button 
                onClick={() => handleVote('up')}
                className={`btn btn-ghost btn-sm p-0 min-h-0 h-8 ${
                  userVote === 'up' 
                    ? 'text-success' 
                    : 'hover:text-success'
                }`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill={userVote === 'up' ? 'currentColor' : 'none'} viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                </svg>
              </button>
              <span className="text-lg font-bold my-2">
                {(discussion.upvotes || 0) - (discussion.downvotes || 0)}
              </span>
              <button 
                onClick={() => handleVote('down')}
                className={`btn btn-ghost btn-sm p-0 min-h-0 h-8 ${
                  userVote === 'down' 
                    ? 'text-error' 
                    : 'hover:text-error'
                }`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill={userVote === 'down' ? 'currentColor' : 'none'} viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
            
            {/* Main Content */}
            <div className="flex-1 p-6">
          {/* Header */}
          <div className="flex items-start gap-4 mb-4">
            <div className="avatar placeholder">
              <div className="bg-neutral text-neutral-content rounded-full w-16 h-16">
                <span className="text-2xl">
                  {discussion.author.firstName[0]}{discussion.author.lastName[0]}
                </span>
              </div>
            </div>
            <div className="flex-1">
              <h1 className="text-3xl font-bold mb-2 flex items-center gap-2">
                {discussion.isPinned && <span className="text-warning">📌</span>}
                {discussion.title}
                {discussion.isLocked && <span className="text-error">🔒</span>}
              </h1>
              <p className="text-sm text-base-content/70">
                by {discussion.author.firstName} {discussion.author.lastName} • {formatDate(discussion.createdAt)}
              </p>
            </div>
          </div>

          {/* Tags/Links */}
          <div className="flex flex-wrap gap-2 mb-4">
            {discussion.boardGameLinks?.map((link) => (
              <Link
                key={link.boardGame.id}
                to={`/board-games/${link.boardGame.slug}`}
                className="badge badge-primary badge-lg"
              >
                🎲 {link.boardGame.name}
              </Link>
            ))}
            {discussion.clubLinks?.map((link) => (
              <Link
                key={link.club.id}
                to={`/clubs/${link.club.slug || link.club.id}`}
                className="badge badge-secondary badge-lg"
              >
                🏛️ {link.club.name}
              </Link>
            ))}
            {discussion.isGeneral && (
              <span className="badge badge-accent badge-lg">General</span>
            )}
          </div>

          {/* Content */}
          <div className="prose max-w-none mb-4">
            <p className="whitespace-pre-wrap">{discussion.content}</p>
          </div>

          {/* Stats */}
          <div className="flex gap-6 text-sm text-base-content/70 border-t pt-4">
            <span>👁️ {discussion.viewCount} views</span>
            <span>💬 {discussion.replies?.length || 0} replies</span>
          </div>
            </div>
          </div>
        </motion.div>

        {/* Replies */}
        <div className="space-y-4 mb-6">
          {discussion.replies && discussion.replies.length > 0 ? (
            discussion.replies.map((reply, index) => (
              <motion.div
                key={reply.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-base-100 rounded-lg shadow-lg p-6"
              >
                <div className="flex items-start gap-4">
                  <div className="avatar placeholder">
                    <div className="bg-neutral text-neutral-content rounded-full w-12 h-12">
                      <span className="text-lg">
                        {reply.author.firstName[0]}{reply.author.lastName[0]}
                      </span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="font-semibold">
                          {reply.author.firstName} {reply.author.lastName}
                        </p>
                        <p className="text-xs text-base-content/70">
                          {formatDate(reply.createdAt)}
                        </p>
                      </div>
                    </div>
                    <p className="whitespace-pre-wrap">{reply.content}</p>
                  </div>
                </div>
              </motion.div>
            ))
          ) : (
            <div className="bg-base-100 rounded-lg shadow-lg p-8 text-center">
              <p className="text-base-content/70">No replies yet. Be the first to reply!</p>
            </div>
          )}
        </div>

        {/* Reply Form */}
        {!discussion.isLocked ? (
          <div className="bg-base-100 rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-bold mb-4">Add a Reply</h2>
            {authService.isAuthenticated() ? (
              <form onSubmit={handleSubmitReply}>
                <div className="form-control mb-4">
                  <textarea
                    value={replyContent}
                    onChange={(e) => setReplyContent(e.target.value)}
                    className="textarea textarea-bordered h-32"
                    placeholder="Write your reply..."
                    required
                  ></textarea>
                </div>
                <button
                  type="submit"
                  disabled={submitting || !replyContent.trim()}
                  className="btn btn-primary"
                >
                  {submitting ? 'Submitting...' : 'Submit Reply'}
                </button>
              </form>
            ) : (
              <div className="text-center">
                <p className="mb-4">You must be logged in to reply</p>
                <Link to="/login" className="btn btn-primary">
                  Login
                </Link>
              </div>
            )}
          </div>
        ) : (
          <div className="alert alert-warning">
            <span>🔒 This discussion is locked. No new replies can be added.</span>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default DiscussionDetail;
